import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import expenseCategoryMasterService from '../../services/expenseCategoryMasterService'

const ExpenseCategoryMasterForm = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    categoryCode: '',
    categoryName: '',
    categoryDescription: '',
    status: 'Active',
    defaultLimits: {
      dailyLimit: 0,
      monthlyLimit: 0,
      perTransactionLimit: 0
    },
    isTaxable: false,
    taxRate: 0,
    requiresReceipt: true,
    requiresApproval: false,
    categoryType: 'Other',
    sortOrder: 0,
    notes: ''
  })
  const [isLoadingCategoryCode, setIsLoadingCategoryCode] = useState(true)
  const [showHelp, setShowHelp] = useState(false)

  // Fetch next category code
  useEffect(() => {
    const fetchCategoryCode = async () => {
      try {
        setIsLoadingCategoryCode(true)
        const response = await expenseCategoryMasterService.getNextCategoryCode()
        if (response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            categoryCode: response.data.categoryCode || response.data.formattedValue || 'ECAT0001'
          }))
        }
      } catch (error) {
        console.error('Error fetching category code:', error)
        setFormData(prev => ({ ...prev, categoryCode: 'ECAT0001' }))
      } finally {
        setIsLoadingCategoryCode(false)
      }
    }
    fetchCategoryCode()
  }, [])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name.startsWith('defaultLimits.')) {
      const limitField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        defaultLimits: {
          ...prev.defaultLimits,
          [limitField]: parseFloat(value) || 0
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.categoryName) {
        toast.error('Please fill in all required fields')
        return
      }

      const response = await expenseCategoryMasterService.createCategory(formData)
      if (response.success) {
        toast.success('Expense category created successfully!')
        navigate('/expenses/expense-category-master')
      } else {
        toast.error(response.message || 'Failed to create category')
      }
    } catch (error) {
      console.error('Error saving category:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        toast.error('Failed to create category')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      categoryCode: formData.categoryCode, // Keep the code
      categoryName: '',
      categoryDescription: '',
      status: 'Active',
      defaultLimits: {
        dailyLimit: 0,
        monthlyLimit: 0,
        perTransactionLimit: 0
      },
      isTaxable: false,
      taxRate: 0,
      requiresReceipt: true,
      requiresApproval: false,
      categoryType: 'Other',
      sortOrder: 0,
      notes: ''
    })
  }

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Expense Category Master', path: '/expenses/expense-category-master', onClick: () => navigate('/expenses/expense-category-master') },
    { label: 'New Category', path: '/expenses/expense-category-master/form', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>What is Expense Category Master?</h3>
      <p>This screen lets you <span className="help-term">create a new expense category</span>—a type of expense (e.g. Travel, Meals, Office) that can be used when recording expenses. Categories control how expenses are grouped, whether they need receipts or approval, and optional spending limits.</p>

      <h3>Navigation</h3>
      <p>Use the <span className="help-term">back arrow</span> in the breadcrumb to go to the previous page. The trail (Expenses &gt; Expense Category Master &gt; New Category) shows your location. Use the <span className="help-term">Back</span> button to return to the category list without saving.</p>

      <h3>Category Information</h3>
      <ul>
        <li><strong>Category Code *</strong> – Auto-generated unique code (e.g. ECT/0002). Read-only; used to identify the category in the system.</li>
        <li><strong>Category Name *</strong> – Display name for the category (required).</li>
        <li><strong>Status *</strong> – <em>Active</em> (category can be used) or <em>Inactive</em> (hidden from use).</li>
        <li><strong>Category Type</strong> – Grouping: Travel, Meals, Office, Communication, Training, Equipment, or Other.</li>
        <li><strong>Category Description</strong> – Optional text describing when and how to use this category.</li>
      </ul>

      <h3>Limits</h3>
      <p>Optional default limits (use 0 for no limit):</p>
      <ul>
        <li><strong>Daily Limit</strong> – Maximum amount allowed per day for this category.</li>
        <li><strong>Monthly Limit</strong> – Maximum amount allowed per month.</li>
        <li><strong>Per Transaction Limit</strong> – Maximum amount per single expense entry.</li>
        <li><strong>Sort Order</strong> – Number used to control display order in lists (lower first).</li>
      </ul>

      <h3>Tax and compliance</h3>
      <ul>
        <li><strong>Is Taxable</strong> – When checked, expenses in this category can have tax applied.</li>
        <li><strong>Tax Rate (%)</strong> – Default tax percentage; only used when Is Taxable is checked.</li>
        <li><strong>Requires Receipt</strong> – When checked, users must attach a receipt for expenses in this category.</li>
        <li><strong>Requires Approval</strong> – When checked, expenses in this category need approval before being finalised.</li>
      </ul>

      <h3>Notes and actions</h3>
      <p><strong>Notes</strong> – Optional internal notes about the category. <strong>Reset</strong> clears the form (keeps the category code). <strong>Save</strong> creates the category and returns you to the Expense Category Master list.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Open help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Expense Category Master</h1>
          <p>Create new expense category</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/expenses/expense-category-master')}>
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Category Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Category Details</h3>
          </div>

          {/* Row 1: Category Code, Category Name, Status, Category Type */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="categoryCode" className="form-label required">
                Category Code
              </label>
              <input
                type="text"
                id="categoryCode"
                name="categoryCode"
                value={isLoadingCategoryCode ? 'Generating...' : formData.categoryCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoryName" className="form-label required">
                Category Name
              </label>
              <input
                type="text"
                id="categoryName"
                name="categoryName"
                value={formData.categoryName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter category name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="categoryType" className="form-label">
                Category Type
              </label>
              <select
                id="categoryType"
                name="categoryType"
                value={formData.categoryType}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Travel">Travel</option>
                <option value="Meals">Meals</option>
                <option value="Office">Office</option>
                <option value="Communication">Communication</option>
                <option value="Training">Training</option>
                <option value="Equipment">Equipment</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Row 2: Category Description */}
          <div className="form-group">
            <label htmlFor="categoryDescription" className="form-label">
              Category Description
            </label>
            <textarea
              id="categoryDescription"
              name="categoryDescription"
              value={formData.categoryDescription}
              onChange={handleInputChange}
              className="form-input"
              rows="2"
              placeholder="Enter category description"
            />
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Default Limits</h3>
            </div>
          </div>

          {/* Row 3: Default Limits */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="defaultLimits.dailyLimit" className="form-label">
                Daily Limit
              </label>
              <input
                type="number"
                id="defaultLimits.dailyLimit"
                name="defaultLimits.dailyLimit"
                value={formData.defaultLimits.dailyLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="defaultLimits.monthlyLimit" className="form-label">
                Monthly Limit
              </label>
              <input
                type="number"
                id="defaultLimits.monthlyLimit"
                name="defaultLimits.monthlyLimit"
                value={formData.defaultLimits.monthlyLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="defaultLimits.perTransactionLimit" className="form-label">
                Per Transaction Limit
              </label>
              <input
                type="number"
                id="defaultLimits.perTransactionLimit"
                name="defaultLimits.perTransactionLimit"
                value={formData.defaultLimits.perTransactionLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sortOrder" className="form-label">
                Sort Order
              </label>
              <input
                type="number"
                id="sortOrder"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Tax &amp; Approval Rules</h3>
            </div>
          </div>

          {/* Row 4: Tax and Approval Settings */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="isTaxable" className="form-label">
                <input
                  type="checkbox"
                  id="isTaxable"
                  name="isTaxable"
                  checked={formData.isTaxable}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Is Taxable
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="taxRate" className="form-label">
                Tax Rate (%)
              </label>
              <input
                type="number"
                id="taxRate"
                name="taxRate"
                value={formData.taxRate}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                max="100"
                step="0.01"
                placeholder="0.00"
                disabled={!formData.isTaxable}
              />
            </div>

            <div className="form-group">
              <label htmlFor="requiresReceipt" className="form-label">
                <input
                  type="checkbox"
                  id="requiresReceipt"
                  name="requiresReceipt"
                  checked={formData.requiresReceipt}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Requires Receipt
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="requiresApproval" className="form-label">
                <input
                  type="checkbox"
                  id="requiresApproval"
                  name="requiresApproval"
                  checked={formData.requiresApproval}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Requires Approval
              </label>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-input"
              rows="3"
              placeholder="Enter any additional notes"
            />
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>

      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Expense Category Master – Help"
      >
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ExpenseCategoryMasterForm

